import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Calendar, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { FeeStructure } from "../../types/fee";
import {
  assignFeeToStudent,
  calculatePaymentSchedule,
} from "../../services/fees";
import { getStudents } from "../../services/students";

interface FeeAssignmentModalProps {
  feeStructure: FeeStructure;
  onClose: () => void;
  onSuccess: () => void;
}

const FeeAssignmentModal: React.FC<FeeAssignmentModalProps> = ({
  feeStructure,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [joinDate, setJoinDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [useInstallments, setUseInstallments] = useState(
    feeStructure.installmentPlan?.enabled || false
  );
  const [customInstallments, setCustomInstallments] = useState({
    numberOfInstallments:
      feeStructure.installmentPlan?.numberOfInstallments || 4,
    frequency: feeStructure.installmentPlan?.frequency || ("monthly" as const),
  });
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountReason, setDiscountReason] = useState("");
  const [previewSchedule, setPreviewSchedule] = useState<any>(null);

  // Fetch students for selection
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ["students"],
    queryFn: () => getStudents({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Preview payment schedule
  const { mutate: previewPaymentSchedule, isPending: isCalculating } =
    useMutation({
      mutationFn: () =>
        calculatePaymentSchedule(
          feeStructure._id,
          joinDate,
          useInstallments
            ? {
                enabled: true,
                numberOfInstallments: customInstallments.numberOfInstallments,
                frequency: customInstallments.frequency,
              }
            : { enabled: false }
        ),
      onSuccess: (data) => {
        setPreviewSchedule(data.data);
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to calculate payment schedule");
      },
    });

  // Assign fees to students
  const { mutate: assignFees, isPending: isAssigning } = useMutation({
    mutationFn: async () => {
      const assignments = selectedStudents.map((studentId) => ({
        feeStructureId: feeStructure._id,
        studentId,
        joinDate,
        customInstallmentPlan: useInstallments
          ? {
              enabled: true,
              numberOfInstallments: customInstallments.numberOfInstallments,
              frequency: customInstallments.frequency,
            }
          : { enabled: false },
        discountAmount: discountAmount > 0 ? discountAmount : undefined,
        discountReason: discountReason || undefined,
      }));

      // Assign to each student individually
      const results = await Promise.all(
        assignments.map((assignment) => assignFeeToStudent(assignment))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fees"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      toast.success(
        `Fee structure assigned to ${selectedStudents.length} student(s) successfully`
      );
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to assign fee structure");
    },
  });

  // Calculate preview when parameters change
  React.useEffect(() => {
    if (joinDate) {
      previewPaymentSchedule();
    }
  }, [joinDate, useInstallments, customInstallments, previewPaymentSchedule]);

  const handleStudentToggle = (studentId: string) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }
    assignFees();
  };

  const finalAmount = feeStructure.totalAmount - discountAmount;

  return (
    <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Assign Fee Structure to Students
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Fee Structure Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">
              {feeStructure.name ||
                `Fee Structure ${feeStructure._id.slice(-6)}`}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Total Amount:</span>
                <p className="text-blue-900">
                  KSh {feeStructure.totalAmount.toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Billing:</span>
                <p className="text-blue-900">{feeStructure.billingFrequency === 'monthly' ? 'Monthly' : feeStructure.academicTerm}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">
                  Academic Year:
                </span>
                <p className="text-blue-900">{feeStructure.academicYear}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Components:</span>
                <p className="text-blue-900">
                  {feeStructure.feeComponents.length} components
                </p>
              </div>
            </div>
          </div>

          {/* Student Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Select Students
            </h3>
            {studentsLoading ? (
              <div className="text-center py-4">Loading students...</div>
            ) : (
              <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                {studentsData?.data?.map((student) => (
                  <div
                    key={student._id}
                    className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-200"
                  >
                    <input
                      type="checkbox"
                      id={`student-${student._id}`}
                      checked={selectedStudents.includes(student._id)}
                      onChange={() => handleStudentToggle(student._id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`student-${student._id}`}
                      className="ml-3 flex-1 cursor-pointer"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {student.userInfo?.firstName}{" "}
                        {student.userInfo?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {student.studentId || "N/A"} • Email:{" "}
                        {student.userInfo?.email || "N/A"}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm text-gray-600 mt-2">
              Selected: {selectedStudents.length} student
              {selectedStudents.length !== 1 ? "s" : ""}
            </p>
          </div>

          {/* Payment Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Payment Configuration
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Join Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={joinDate}
                    onChange={(e) => setJoinDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    First payment will be due from this date
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Amount (Optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={feeStructure.totalAmount}
                    step="0.01"
                    value={discountAmount}
                    onChange={(e) =>
                      setDiscountAmount(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>

                {discountAmount > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Reason
                    </label>
                    <input
                      type="text"
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Scholarship, Early bird discount"
                    />
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="useInstallments"
                    checked={useInstallments}
                    onChange={(e) => setUseInstallments(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="useInstallments"
                      className="text-sm font-medium text-gray-700"
                    >
                      Enable Installment Payments
                    </label>
                    <p className="text-xs text-gray-500">
                      Allow students to pay in installments based on their join
                      date
                    </p>
                  </div>
                </div>

                {useInstallments && (
                  <div className="ml-7 space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Installments
                        </label>
                        <input
                          type="number"
                          min="2"
                          max="12"
                          value={customInstallments.numberOfInstallments || ""}
                          placeholder="4"
                          onChange={(e) =>
                            setCustomInstallments((prev) => ({
                              ...prev,
                              numberOfInstallments:
                                e.target.value === ""
                                  ? ""
                                  : parseInt(e.target.value) || 4,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Frequency
                        </label>
                        <select
                          value={customInstallments.frequency}
                          onChange={(e) =>
                            setCustomInstallments((prev) => ({
                              ...prev,
                              frequency: e.target.value as
                                | "monthly"
                                | "quarterly"
                                | "weekly",
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Schedule Preview */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Payment Schedule Preview
              </h3>

              {isCalculating ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-2 text-gray-600">Calculating schedule...</p>
                </div>
              ) : previewSchedule ? (
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-green-700 font-medium">
                          Total Amount:
                        </span>
                        <p className="text-green-900">
                          KSh {finalAmount.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">
                          Per Installment:
                        </span>
                        <p className="text-green-900">
                          KSh{" "}
                          {previewSchedule.installmentAmount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Payment Timeline:
                    </h4>
                    <div className="space-y-2">
                      {previewSchedule.paymentSchedule.map(
                        (installment: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                          >
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="font-medium">
                                Installment {installment.installmentNumber}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                KSh {installment.amount.toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                Due:{" "}
                                {new Date(
                                  installment.dueDate
                                ).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Set join date to preview payment schedule</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isAssigning}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isAssigning || selectedStudents.length === 0}
            >
              {isAssigning ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Assigning...
                </div>
              ) : (
                `Assign to ${selectedStudents.length} Student${
                  selectedStudents.length !== 1 ? "s" : ""
                }`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeeAssignmentModal;
